<template>
  <!--prettier ignore-->
  <div
    class="accordion"
    :class="{ 'accordion--open': showContent, 'accordion--disabled': $props.disabled }"
  >
    <div
      class="accordion__header"
      @click="toggleContent"
    >
      <div class="header__data">
        <the-icon
          v-if="$props.infoText"
          v-tooltip="$props.infoText"
          art="regular"
          class="info__icon"
          name="circle-info"
        />
        <div class="header__text">
          <template v-if="$props.heading">
            <span
              class="title-1"
              v-html="$props.heading"
            />
            <span
              v-if="$props.subline"
              class="caption-regular"
              v-html="$props.subline"
            />
          </template>
          <template v-else>
            <slot name="heading" />
          </template>
        </div>
      </div>
      <the-icon
        art="solid"
        class="header__icon"
        :name="iconName"
      />
    </div>
    <Transition
      name="slide"
      :duration="400"
    >
      <div
        v-if="showContent"
        class="accordion__content"
      >
        <div class="content__container">
          <slot></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from 'vue'

  // INIT
  const props = defineProps({
    hasChevronIcon: {
      type: Boolean,
      default: false,
    },

    heading: {
      type: String,
      default: null,
    },

    infoText: {
      type: String,
      default: '',
    },

    subline: {
      type: String,
      default: '',
    },

    active: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  })

  // DATA
  const showContent = ref(props.disabled ? false : props.active)

  // COMPUTED
  const iconName = computed(() => {
    if (props.hasChevronIcon) {
      return showContent.value ? 'chevron-down' : 'chevron-up'
    } else {
      return showContent.value ? 'minus' : 'plus'
    }
  })

  // METHODS
  function toggleContent() {
    if (props.disabled) return
    showContent.value = !showContent.value
  }

  // WATCHER
  watch(
    () => props.active,
    val => {
      showContent.value = props.disabled ? false : val
    }
  )
</script>

<style scoped>
.slide-enter-active {
  transition-delay: 0ms;
  transition-property: max-height;
  transition-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
  transition-duration: 300ms;
  max-height: 100vh;
}

.slide-leave-active {
  transition-delay: 100ms;
  transition-property: max-height;
  transition-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
  transition-duration: 300ms;
  max-height: 100vh;
}

.slide-enter-active .content__container {
  transition-delay: 300ms;
  transition-property: opacity;
  transition-timing-function: linear;
  transition-duration: 100ms;
}

.slide-leave-active .content__container {
  transition-delay: 0ms;
  transition-property: opacity;
  transition-timing-function: linear;
  transition-duration: 100ms;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
}

.slide-enter-from .content__container,
.slide-leave-to .content__container {
  opacity: 0;
}
</style>

<style scoped>
.accordion {
  padding: 0 0 var(--dotcom-responsive-spacing-res-m);
  border-bottom: 1px solid var(--on-surface);

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header__data {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--dotcom-responsive-spacing-res-xxxs);

      .info__icon.icon {
        font-size: 20px;
        color: var(--on-surface);
      }
    }

    .header__text {
      display: flex;
      flex-direction: column;
    }

    .header__icon.icon {
      font-size: 24px;
      color: var(--primary);
    }
  }

  &__content {
    margin: var(--dotcom-responsive-spacing-res-l) 0 0;
  }
}

.accordion:not(.accordion--disabled) .accordion__header {
  cursor: pointer;
}

.accordion--disabled .accordion__header {
  color: var(--Interaction-States-disabled-default-label);
}

.accordion--disabled .header__icon.icon {
  color: var(--Interaction-States-disabled-default);
}
</style>
